import { MegaphoneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { useEffect, useState } from "react";
import {
  MID_YEAR_BANNER_DESKTOP,
  MID_YEAR_BANNER_MOBILE,
  MID_YEAR_PAGE_PATH,
  buildMidYearCallbackPath,
  isMidYearPromotionVisible,
  trackMidYearEvent,
} from "@/utils/mid-year-career-upgrade";
import {
  SUMMER_SPONSORSHIP_PAGE_PATH,
  getSummerSponsorshipPhase,
  isSummerSponsorshipPromotionVisible,
} from "@/utils/summer-sponsorship-2026";
import { track } from "@/utils/conversions";
import { isSelloutBannerVisible, SELLOUT_BANNER_KEY, SELLOUT_PAGE_PATH } from "@/lib/sellout-offer";

const MID_YEAR_BANNER_KEY = "cc.guidance.banner.mid-year-career-upgrade";
const LAST_MINUTE_BANNER_KEY =
  "cc.guidance.banner.last-minute-august-offers-2026-v3";
const LAST_MINUTE_PAGE_PATH = "/last-minute-offers/";
const CPD_PACKAGE_PATH = "/build-your-cpd-package/";

type ActiveBanner = "sellout" | "mid-year" | "last-minute" | null;

function resolveActiveBanner(): ActiveBanner {
  if (isSelloutBannerVisible()) {
    return "sellout";
  }

  if (isMidYearPromotionVisible()) {
    return "mid-year";
  }

  if (isSummerSponsorshipPromotionVisible()) {
    return "last-minute";
  }

  return null;
}

function getLastMinuteCtas() {
  const phase = getSummerSponsorshipPhase();
  const percent = phase?.sponsorshipPercent ?? 20;
  const code = phase?.discountCode ?? "SPONSOR20";

  return [
    {
      href: SUMMER_SPONSORSHIP_PAGE_PATH,
      label: `${percent}% Off August VTCT`,
      detail: `Code ${code}`,
      eventLabel: `${code.toLowerCase()}-august-vtct`,
    },
    {
      href: CPD_PACKAGE_PATH,
      label: "4 CPD for £999",
      detail: null,
      eventLabel: "cpd-package-999",
    },
    {
      href: LAST_MINUTE_PAGE_PATH,
      label: "Last Minute Offers",
      detail: null,
      eventLabel: "last-minute-offers",
    },
  ] as const;
}

export default function FixedBanner({ router }) {
  // Resolve campaign on first render (SSR + client) so the bar does not pop
  // in after hydration and push the whole page down (CLS).
  const [activeBanner] = useState<ActiveBanner>(() => resolveActiveBanner());
  // Assume not dismissed until localStorage can be read. Dismissed visitors may
  // see a brief hide; that is far less harmful than showing the bar late.
  const [dismiss, setDismissed] = useState(false);

  useEffect(() => {
    if (activeBanner === "sellout") {
      setDismissed(!!localStorage.getItem(SELLOUT_BANNER_KEY));
    } else if (activeBanner === "mid-year") {
      setDismissed(!!localStorage.getItem(MID_YEAR_BANNER_KEY));
    } else if (activeBanner === "last-minute") {
      setDismissed(!!localStorage.getItem(LAST_MINUTE_BANNER_KEY));
    }
  }, [activeBanner]);

  const campaignPath =
    activeBanner === "sellout"
      ? SELLOUT_PAGE_PATH
      : activeBanner === "last-minute"
      ? LAST_MINUTE_PAGE_PATH
      : MID_YEAR_PAGE_PATH;

  if (
    !activeBanner ||
    dismiss ||
    router.pathname.includes("booking") ||
    router.asPath.startsWith(campaignPath) ||
    router.asPath.includes("/checkout") ||
    router.asPath.includes("/book") ||
    router.asPath.includes("/enrollment/quote")
  ) {
    return null;
  }

  const close = () => {
    const key =
      activeBanner === "sellout"
        ? SELLOUT_BANNER_KEY
        : activeBanner === "last-minute"
        ? LAST_MINUTE_BANNER_KEY
        : MID_YEAR_BANNER_KEY;
    localStorage.setItem(key, "y");
    setDismissed(true);
  };

  if (activeBanner === "sellout") {
    return (
      <div className="bg-gray-950">
        <div className="mx-auto max-w-7xl py-3 px-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-sm font-medium text-white">
              Book a 2026 course date today. If Black Friday beats your price, we'll credit the difference.
            </p>
            <div className="flex items-center gap-2">
              <Link
                href={SELLOUT_PAGE_PATH}
                className="inline-flex items-center justify-center rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
              >
                See 2026 dates
              </Link>
              <button
                type="button"
                onClick={close}
                className="rounded-md p-2 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white"
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="size-4 text-white" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (activeBanner === "last-minute") {
    const lastMinuteCtas = getLastMinuteCtas();

    return (
      <div className="bg-purple-800">
        <div className="mx-auto max-w-7xl px-3 py-2.5 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-3">
              {lastMinuteCtas.map((cta) => (
                <Link
                  key={cta.href}
                  href={cta.href}
                  onClick={() => {
                    track("top_banner_cta_click", {
                      event_category: "Top Banner",
                      event_label: cta.eventLabel,
                      placement: "fixed-banner",
                    });
                  }}
                  className="flex min-h-[2.75rem] flex-col items-center justify-center rounded-md border border-transparent bg-white px-3 py-2 text-center shadow-sm hover:bg-purple-50"
                >
                  <span className="text-sm font-semibold leading-tight text-purple-900">
                    {cta.label}
                  </span>
                  {cta.detail ? (
                    <span className="mt-0.5 text-[11px] font-medium uppercase tracking-wide text-purple-700">
                      {cta.detail}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
            <button
              type="button"
              onClick={close}
              className="flex shrink-0 rounded-md p-2 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-white"
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="size-4 text-white" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-midyear-800">
      <div className="mx-auto max-w-7xl py-3 px-3 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-center">
            <span className="flex rounded-lg bg-midyear-700 p-2">
              <MegaphoneIcon
                className="size-4 text-white"
                aria-hidden="true"
              />
            </span>
            <p className="ml-3 font-medium text-white text-sm">
              <span className="md:hidden">{MID_YEAR_BANNER_MOBILE}</span>
              <span className="hidden md:inline">{MID_YEAR_BANNER_DESKTOP}</span>
            </p>
          </div>
          <div className="flex w-full flex-shrink-0 items-center gap-2 sm:order-2 sm:ml-auto sm:w-auto">
            <Link
              href={MID_YEAR_PAGE_PATH}
              onClick={() => {
                trackMidYearEvent("mid_year_page_click", {
                  placement: "fixed-banner",
                });
              }}
              className="flex flex-1 items-center justify-center rounded-md border border-transparent bg-white px-3 py-1.5 text-sm font-medium text-midyear-800 shadow-sm hover:bg-midyear-50 sm:flex-none"
            >
              View event details
            </Link>
            <Link
              href={buildMidYearCallbackPath("fixed-banner")}
              className="hidden items-center justify-center rounded-md border border-white/30 px-3 py-1.5 text-sm font-medium text-white hover:bg-midyear-700 sm:inline-flex"
            >
              Talk to admissions
            </Link>
          </div>
          <div className="order-first flex-shrink-0 sm:order-3 sm:ml-1">
            <button
              type="button"
              onClick={close}
              className="-mr-1 flex rounded-md p-2 hover:bg-midyear-700 focus:outline-none focus:ring-2 focus:ring-white sm:-mr-2"
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="size-4 text-white" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
